import React, { useEffect, useState } from "react";
import { API } from "./shared";
import { Loader2, TrendingUp, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

// Small SVG sparkline of School-vs-Portfolio variance across periods.
const Spark = ({ points, maxAbs }) => {
  const W = 240, H = 54, pad = 8;
  const n = points.length;
  const zeroY = H / 2;
  const scale = maxAbs > 0 ? (H / 2 - pad) / maxAbs : 0;
  const x = (i) => (n === 1 ? W / 2 : pad + (i * (W - pad * 2)) / (n - 1));
  const y = (v) => zeroY - v * scale;
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.variance).toFixed(1)}`).join(" ");
  return (
    <svg width={W} height={H} className="overflow-visible">
      <line x1={pad} y1={zeroY} x2={W - pad} y2={zeroY} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3 3" />
      {n > 1 && <path d={path} fill="none" stroke="#94a3b8" strokeWidth="1.5" />}
      {points.map((p, i) => {
        const up = p.variance > 0.05, down = p.variance < -0.05;
        const color = up ? "#2563eb" : down ? "#007a9e" : "#94a3b8";
        return (
          <g key={p.period_id}>
            <circle cx={x(i)} cy={y(p.variance)} r="3.5" fill={color} />
            <text x={x(i)} y={y(p.variance) + (p.variance >= 0 ? -8 : 15)} textAnchor="middle"
              fontSize="9" fill={color} fontWeight="600">
              {p.variance > 0 ? "+" : ""}{p.variance}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default function GapTrend({ pid, tenantId, title = "Gap over time" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pid) return;
    (async () => {
      setLoading(true);
      try {
        const url = tenantId
          ? `${API}/api/portfolio/${pid}/schools/${tenantId}/gap-trend`
          : `${API}/api/portfolio/${pid}/gap-trend`;
        const r = await fetch(url, { credentials: "include" });
        if (r.ok) setData(await r.json());
      } finally { setLoading(false); }
    })();
  }, [pid, tenantId]);

  if (loading) return <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-slate-300" /></div>;
  const schools = data?.schools || [];
  const periodCount = (data?.periods || []).length;
  if (schools.length === 0) return null;

  return (
    <div data-testid="gap-trend">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="h-4 w-4 text-slate-500" />
        <h2 className="text-base font-semibold text-slate-700">{title}</h2>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Variance (Portfolio − School) per diagnostic period.{" "}
        <span className="text-blue-700">positive = portfolio rated higher</span>,{" "}
        <span className="text-[#007a9e]">negative = portfolio rated lower</span>.
        {periodCount < 2 && " Runs build into a trend as you add more periods."}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {schools.map((s) => {
          const pts = s.points || [];
          const maxAbs = Math.max(1, ...pts.map((p) => Math.abs(p.variance)));
          const first = pts[0]?.variance ?? null;
          const last = pts[pts.length - 1]?.variance ?? null;
          const delta = first !== null && last !== null ? +(last - first).toFixed(1) : null;
          const Dir = delta === null || Math.abs(delta) < 0.05 ? Minus : delta > 0 ? ArrowUpRight : ArrowDownRight;
          const dirCls = delta === null || Math.abs(delta) < 0.05 ? "text-slate-400" : delta > 0 ? "text-blue-600" : "text-[#007a9e]";
          return (
            <div key={s.tenant_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`gap-trend-school-${s.tenant_id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-medium text-slate-800 text-sm truncate">{s.school_name}</span>
                {pts.length > 1 && (
                  <span className={`inline-flex items-center gap-0.5 text-xs ${dirCls}`}>
                    <Dir className="h-3.5 w-3.5" />{delta > 0 ? "+" : ""}{delta}
                  </span>
                )}
              </div>
              <Spark points={pts} maxAbs={maxAbs} />
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-400">
                {pts.map((p) => <span key={p.period_id} className="truncate max-w-[7rem]">{p.label}</span>)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
